import type { SVGProps } from "react";
import { assetUrl } from "../lib/assets";
import { trackEvent } from "../lib/analytics";

interface OnboardingLandingPageProps {
  isSigningIn?: boolean;
  onSignInAsUser: () => void;
  onSignInAsDietitian: () => void;
}

function IconGlobe(props: SVGProps<SVGSVGElement>) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" {...props}>
      <circle cx="12" cy="12" r="9" />
      <path d="M3 12h18" />
      <path d="M12 3a14.5 14.5 0 0 1 0 18" />
      <path d="M12 3a14.5 14.5 0 0 0 0 18" />
    </svg>
  );
}

function IconUser(props: SVGProps<SVGSVGElement>) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" {...props}>
      <circle cx="12" cy="8" r="4" />
      <path d="M4.5 20.5C5.4 16.8 8.1 15 12 15s6.6 1.8 7.5 5.5" />
    </svg>
  );
}

function IconArrow(props: SVGProps<SVGSVGElement>) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...props}>
      <path d="m9 6 6 6-6 6" />
    </svg>
  );
}

function IconLock(props: SVGProps<SVGSVGElement>) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" {...props}>
      <rect x="4.5" y="10.5" width="15" height="10" rx="2.5" />
      <path d="M8.5 10.5V8a3.5 3.5 0 1 1 7 0v2.5" />
    </svg>
  );
}

function IconShield(props: SVGProps<SVGSVGElement>) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" {...props}>
      <path d="M12 3.5 5.5 6v5.9c0 4.3 2.7 7.8 6.5 8.6 3.8-.8 6.5-4.3 6.5-8.6V6L12 3.5Z" />
      <path d="m9.2 11.8 2.1 2.1 3.6-3.6" />
    </svg>
  );
}

export function OnboardingLandingPage({
  isSigningIn,
  onSignInAsUser,
  onSignInAsDietitian,
}: OnboardingLandingPageProps) {
  return (
    <div data-testid="onboarding-landing" className="prx-onboarding-auth">
      <img
        src={assetUrl("pantry-inventory-bg.png")}
        alt=""
        className="prx-onboarding-auth__bg-image"
        aria-hidden="true"
      />
      <div className="prx-onboarding-auth__bg-tone" aria-hidden="true" />
      <div className="prx-onboarding-auth__bg-glow" aria-hidden="true" />
      <div className="prx-onboarding-auth__vignette" aria-hidden="true" />
      <div className="prx-onboarding-auth__bg-grain" aria-hidden="true" />

      <div className="prx-onboarding-auth__inner">
        <article className="prx-onboarding-auth__card">
          <div className="prx-onboarding-auth__card-top">
            <div className="prx-onboarding-auth__brand">
              <img src={assetUrl("app-icon-logo.png")} alt="PantryRx" className="prx-onboarding-auth__brand-icon" />
              <span className="prx-onboarding-auth__brand-text">PantryRx</span>
            </div>
            <span className="prx-onboarding-auth__language" aria-label="Language and locale">
              <IconGlobe className="prx-onboarding-auth__language-icon" aria-hidden="true" />
              <span>US &middot; EN</span>
            </span>
          </div>

          <header className="prx-onboarding-auth__headline">
            <h1>Welcome Back</h1>
            <p>
              Access your <span>Pantry Dashboard</span>
            </p>
          </header>

          <div className="prx-onboarding-auth__actions">
            <button
              type="button"
              data-testid="onboarding-get-started"
              className="prx-onboarding-auth__button prx-onboarding-auth__button--primary"
              onClick={() => { trackEvent("onboarding_cta_clicked", { role: "user" }); onSignInAsUser(); }}
              disabled={isSigningIn}
            >
              <span className="prx-onboarding-auth__button-icon" aria-hidden="true">
                <IconUser />
              </span>
              <span>{isSigningIn ? "Connecting..." : "Continue as User"}</span>
              <span className="prx-onboarding-auth__button-arrow" aria-hidden="true">
                <IconArrow />
              </span>
            </button>

            <div className="prx-onboarding-auth__divider" aria-hidden="true">
              <span />
              <strong>or</strong>
              <span />
            </div>

            <button
              type="button"
              data-testid="onboarding-sign-in-dietitian"
              className="prx-onboarding-auth__button prx-onboarding-auth__button--secondary"
              onClick={() => { trackEvent("onboarding_cta_clicked", { role: "dietitian" }); onSignInAsDietitian(); }}
              disabled={isSigningIn}
            >
              <span className="prx-onboarding-auth__button-icon" aria-hidden="true">
                <IconUser />
              </span>
              <span>Sign in as Dietitian</span>
              <span className="prx-onboarding-auth__button-spacer" aria-hidden="true" />
            </button>
          </div>

          <footer className="prx-onboarding-auth__security">
            <IconLock className="prx-onboarding-auth__security-icon" aria-hidden="true" />
            <span>Secure</span>
            <IconShield className="prx-onboarding-auth__security-icon" aria-hidden="true" />
          </footer>
        </article>
      </div>
    </div>
  );
}
